import { ModeEnum, type CustomerQuote } from '@talos/kyoko';
import type { RFQCustomerQuotePricingRequest, RFQPricingFormState } from './types';

export function buildCustomerQuotePricingRequest({
  quote,
  formState,
}: {
  quote: CustomerQuote;
  formState: RFQPricingFormState;
}): RFQCustomerQuotePricingRequest {
  if (!quote.QuoteID) {
    throw new Error('Missing QuoteID for pricing request');
  }

  const sizeCounteringEnabled = quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled;
  const bidQty =
    sizeCounteringEnabled && formState.compositeBidAsk.bidQty.hasValue
      ? formState.compositeBidAsk.bidQty.value
      : undefined;
  const offerQty =
    sizeCounteringEnabled && formState.compositeBidAsk.askQty.hasValue
      ? formState.compositeBidAsk.askQty.value
      : undefined;

  const baseRequestParams: Pick<RFQCustomerQuotePricingRequest, 'QuoteID' | 'ClearingAccount'> = {
    QuoteID: quote.QuoteID,
    ClearingAccount: formState.clearingAccountField.value,
  };

  if (!quote.LegSummary || quote.LegSummary.length === 0) {
    // No legs, just basic bid-ask
    const bidPx = formState.compositeBidAsk.bid.hasValue ? formState.compositeBidAsk.bid.value : undefined;
    const offerPx = formState.compositeBidAsk.ask.hasValue ? formState.compositeBidAsk.ask.value : undefined;
    if (!bidPx && !offerPx) {
      throw new Error('At least one of BidPx or OfferPx is required');
    }

    return {
      ...baseRequestParams,
      BidPx: bidPx,
      OfferPx: offerPx,
      BidQty: bidQty,
      OfferQty: offerQty,
    };
  }

  // Else, we have legs. We can either be pricing "per-leg" or "composite".
  // If composite, send top-level prices. If per-leg, each leg has its own price.
  if (formState.pricingMethod === 'composite') {
    return {
      ...baseRequestParams,
      BidPx: formState.compositeBidAsk.bid.hasValue ? formState.compositeBidAsk.bid.value : undefined,
      OfferPx: formState.compositeBidAsk.ask.hasValue ? formState.compositeBidAsk.ask.value : undefined,
      BidQty: bidQty,
      OfferQty: offerQty,
    };
  }

  const legPrices = quote.LegSummary.map(leg => {
    const legBidAsk = formState.legBidAsks.find(lba => lba.symbol === leg.Symbol);

    const bidPx = legBidAsk?.bid.hasValue ? legBidAsk.bid.value : undefined;

    const offerPx = legBidAsk?.ask.hasValue ? legBidAsk.ask.value : undefined;

    return {
      Symbol: leg.Symbol,
      BidPx: bidPx,
      OfferPx: offerPx,
    };
  });

  return {
    ...baseRequestParams,
    BidQty: bidQty,
    OfferQty: offerQty,
    LegPrices: legPrices,
  };
}
